import React, { useState, useCallback, useEffect, useMemo } from 'react';
import './WidgetCreator.css';
import { 
  Eye, Copy, Code, Zap, Palette, Layout, Image, 
  Settings, Type, Mail, ChevronRight, Check, 
  X, Plus, Shield, Globe, Smartphone, Maximize2,
  Download, Link, Lock, Sun, Moon, Terminal,
  Cpu, Database, Zap as Lightning, AlertTriangle,
  ToggleLeft, ToggleRight, Eye as EyeIcon, EyeOff, ChevronDown, ChevronUp
} from 'lucide-react';
import { useTheme } from "../ThemeContext";
import LeadMagnets from './ListLeadMagnet';
import { apifetch } from '../services/interceptors';
import { widgetResizeScript, widgetFrameStyle } from '../v2/lib/widgetEmbed';
// Layout options - Updated for new responsive design
const LAYOUTS = [
  { 
    id: 'full', 
    name: 'Full',
    description: 'Complete branded experience',
    icon: 'full',
    style: {
      border: '2px solid #8b5cf6',
      borderRadius: '20px',
      backgroundColor: '#ffffff',
      overflow: 'hidden'
    }
  },
  { 
    id: 'minimal', 
     name: 'Bare Minimum',
    description: 'Simple inline scanner',
    icon: 'minimal',
    style: {
      border: '1px solid #e2e8f0',
      padding: '16px',
      borderRadius: '12px',
      fontSize: '14px',
      backgroundColor: '#ffffff'
    }
  },
];

// Theme options
const THEMES = [
  { id: 'light', name: 'Light', primary: '#8b5cf6', bg: '#ffffff', icon: <Sun size={16} /> },
  { id: 'dark', name: 'Dark', primary: '#a78bfa', bg: '#0f172a', icon: <Moon size={16} /> },
  { id: 'cyber', name: 'Cyber', primary: '#00ff88', bg: '#0a0a0f', icon: <Terminal size={16} /> },
];

// Feature options for hero section
const DEFAULT_FEATURES = [
  'Real-time vulnerability detection',
  'Comprehensive security analysis',
  'Detailed remediation guidance',
  'Enterprise-grade scanning'
];

// Default hero image
const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=800&q=80';

const WidgetPreview = ({ config, layoutStyle, displayMode }) => {
  const renderHeroSection = () => {
    if (config.layout === 'split' && displayMode === 'inline') {
      return (
        <div className="preview-hero-section" style={{ backgroundImage: `url(${config.heroImage || DEFAULT_HERO_IMAGE})` }}>
          <div className="preview-hero-overlay">
            <div className="preview-hero-content">
              {config.logoUrl && (
                <img 
                  src={config.logoUrl} 
                  alt="Company logo" 
                  className="preview-hero-logo"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <h3 className="preview-hero-title">
                {config.heroTitle || 'Secure Your Digital Presence'}
              </h3>
              <p className="preview-hero-subtitle">
                {config.heroSubtitle || 'Get instant security insights with our advanced scanning technology'}
              </p>
              <div className="preview-hero-features">
                {config.features.slice(0, 3).map((feature, index) => (
                  <div key={index} className="preview-feature-item">
                    <Check size={14} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="widget-preview-wrapper">
      {displayMode === 'modal' && (
        <div className="modal-preview-overlay">
          <div 
            className="widget-preview modal-preview" 
            style={layoutStyle}
            role="region"
            aria-label="Widget preview"
          >
            <div className="modal-header">
              <h3 className="modal-title">{config.title || 'Security Scan'}</h3>
              <button className="modal-close-button" aria-label="Close modal">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              {config.layout === 'split' && renderHeroSection()}
              
              <div className={`preview-widget-content ${config.layout === 'split' ? 'split-layout' : ''}`}>
                {config.logoUrl && (
                  <img 
                    src={config.logoUrl} 
                    alt="Company logo" 
                    className="preview-logo-image"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                
                <h4 className="preview-widget-title">{config.title || 'Security Scanner'}</h4>
                {config.subtitle && <p className="preview-widget-subtitle">{config.subtitle}</p>}
                
                <div className="preview-form">
                  <div className="preview-input-group">
                    <span className="preview-input-icon">
                      <Link size={16} />
                    </span>
                    <input
                      type="text"
                      placeholder="https://example.com"
                      className="preview-input"
                      disabled
                      aria-label="Website URL input"
                    />
                  </div>
                  
                  {config.collectEmail && (
                    <div className="preview-input-group">
                      <span className="preview-input-icon">
                        <Mail size={16} />
                      </span>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        className="preview-input"
                        disabled
                        aria-label="Email input"
                      />
                    </div>
                  )}
                  
                    <div className="preview-input-group">
                      <span className="preview-input-icon">
                        <Database size={16} />
                      </span>
                      <input
                        type="text"
                        placeholder="Company Name"
                        className="preview-input"
                        disabled
                        aria-label="Company Name input"
                      />
                    </div>
                  
                  <div className="preview-consent-group">
                    <input type="checkbox" id="preview-consent" disabled />
                    <label htmlFor="preview-consent" className="preview-checkbox-label">
                      I agree to the Terms and Privacy Policy
                    </label>
                  </div>
                  
                  <button
                    className="preview-button"
                    style={{ 
                      background: `linear-gradient(135deg, ${config.buttonColor} 0%, ${config.buttonColor}99 100%)` 
                    }}
                    disabled
                    aria-label={`${config.buttonText} button`}
                  >
                    <Shield size={16} />
                    {config.buttonText}
                  </button>
                </div>
              </div>
            </div>
            <div className="preview-note">
              Modal Preview - Click outside to close
            </div>
          </div>
        </div>
      )}
      
      {displayMode === 'inline' && (
        <div 
          className={`widget-preview ${config.layout === 'split' ? 'split-layout-preview' : ''}`} 
          style={layoutStyle}
          role="region"
          aria-label="Widget preview"
        >
          {renderHeroSection()}
          
          <div className={`preview-widget-content ${config.layout === 'split' ? 'split-layout' : ''}`}>
            {config.logoUrl && config.layout !== 'split' && (
              <img 
                src={config.logoUrl} 
                alt="Company logo" 
                className="preview-logo-image"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            
            <h4 className="preview-widget-title">{config.title || 'Security Scanner'}</h4>
            {config.subtitle && <p className="preview-widget-subtitle">{config.subtitle}</p>}
            
            <div className="preview-form">
              <div className="preview-input-group">
                <span className="preview-input-icon">
                  <Link size={16} />
                </span>
                <input
                  type="text"
                  placeholder="https://example.com"
                  className="preview-input"
                  disabled
                  aria-label="Website URL input"
                />
              </div>
              
              {config.collectEmail && (
                  <div className="preview-input-group">
                    <span className="preview-input-icon">
                      <Mail size={16} />
                    </span>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      className="preview-input"
                      disabled
                      aria-label="Email input"
                    />
                  </div>
                )}
                
                <div className="preview-input-group">
                  <span className="preview-input-icon">
                    <Database size={16} />
                  </span>
                  <input
                    type="text"
                    placeholder="Company Name"
                    className="preview-input"
                    disabled
                    aria-label="Company Name input"
                  />
                </div>
              
              <div className="preview-consent-group">
                <input type="checkbox" id="preview-consent" disabled />
                <label htmlFor="preview-consent" className="preview-checkbox-label">
                  I agree to the Terms and Privacy Policy
                </label>
              </div>
              
              <button
                className="preview-button"
                style={{ 
                  background: `linear-gradient(135deg, ${config.buttonColor} 0%, ${config.buttonColor}99 100%)`,
                  opacity: 0.6
                }}
                disabled
                aria-label={`${config.buttonText} button`}
              >
                <Shield size={16} />
                {config.buttonText}
              </button>
            </div>
            
            <div className="preview-footer">
              <div className="preview-footer-links">
                <a href="#" className="preview-footer-link">Terms</a>
                <span className="preview-footer-separator">•</span>
                <a href="#" className="preview-footer-link">Privacy</a>
                <span className="preview-footer-separator">•</span>
                <a href="#" className="preview-footer-link">Get Full Access</a>
              </div>
              <div className="preview-footer-brand">
                <Lock size={12} />
                Powered by <strong>Pipsqueak NetScan</strong>
              </div>
            </div>
          </div>
          
          <div className="preview-note">
            {config.layout === 'split' ? 'Split Layout Preview (Hero on left on desktop)' : 'Inline Preview'}
          </div>
        </div>
      )}
    </div>
  );
};


const useWidgetConfig = () => {
  const [config, setConfig] = useState({
    // Basic settings
    title: 'Security Scanner',
    subtitle: 'Scan your website for security vulnerabilities',
    logoUrl: '',
    webhookUrl: '',
    
    // Form settings
    buttonText: 'Start Security Scan',
    buttonColor: '#8b5cf6',
    collectEmail: true,
    collectCompany: true,
    
    // Layout & Theme
    layout: 'split',
    theme: 'light',
    
    // Hero section (for split layout)
    heroImage: DEFAULT_HERO_IMAGE,
    heroTitle: 'Secure Your Digital Presence',
    heroSubtitle: 'Get instant security insights with our advanced scanning technology',
    features: DEFAULT_FEATURES,
    
    // Display settings
    displayMode: 'new-tab',
    triggerText: 'Run Security Scan',
    triggerPosition: 'bottom-right',
    autoOpen: false,
    openDelay: 0,
    themeToggle: false,
    
    // Additional options
    backgroundPattern: true,
    backgroundImage: '',
    
    // Colors customization
    colors: {
      primary: '',
      background: '',
      text: ''
    },
  });
  
  const [errors, setErrors] = useState({});

  const validateConfig = useCallback((config) => {
    const newErrors = {};
    
    if (config.logoUrl && !/^https?:\/\/.+\..+/.test(config.logoUrl)) {
      newErrors.logoUrl = 'Please enter a valid URL';
    }

    if (config.webhookUrl && !/^https?:\/\/.+\..+/.test(config.webhookUrl)) {
      newErrors.webhookUrl = 'Please enter a valid URL';
    }
    
    if (config.heroImage && !/^https?:\/\/.+\..+/.test(config.heroImage)) {
      newErrors.heroImage = 'Please enter a valid URL for hero image';
    }
    
    if (!config.buttonText.trim()) {
      newErrors.buttonText = 'Button text is required';
    }
    
    if (!config.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (config.displayMode === 'modal' && !config.triggerText.trim()) {
      newErrors.triggerText = 'Trigger button text is required';
    }
    
    if (config.autoOpen && config.openDelay < 0) {
      newErrors.openDelay = 'Delay must be a positive number';
    }

  
    
    return newErrors;
  }, []);

  const updateConfig = useCallback((updates) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      setErrors(validateConfig(newConfig));
      return newConfig;
    });
  }, [validateConfig]);

  return { config, errors, updateConfig, validateConfig };
};

const WidgetCreator = () => {
  const { config, errors, updateConfig } = useWidgetConfig();
  const [embedCode, setEmbedCode] = useState('');
  const [scriptTag, setScriptTag] = useState('');
  const [copied, setCopied] = useState({ embed: false, script: false });
  const [generatedKey, setGeneratedKey] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const token = localStorage.getItem('token');
  const { isDarkMode } = useTheme();
  const themclass = isDarkMode ? 'widget-creator-dark bg-dark text-light' : 'widget-creator-light';
  

  const selectedLayout = useMemo(
    () => LAYOUTS.find(l => l.id === config.layout) || LAYOUTS[0],
    [config.layout]
  );

  const selectedTheme = useMemo(
    () => THEMES.find(t => t.id === config.theme) || THEMES[0],
    [config.theme]
  );

  const handleGenerateWidget = useCallback(async () => {
    const validationErrors = Object.keys(errors);
    if (validationErrors.length > 0) {
      alert(`Please fix the following errors:\n${validationErrors.join('\n')}`);
      return;
    }
    try {
      const response = await apifetch('/s2/api/widgets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
         body: JSON.stringify({
          ...config,
          // Only send color customization if colors are provided
          colors: config.colors.primary || config.colors.background || config.colors.text 
            ? config.colors 
            : undefined
        }), 
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const widgetKey = data.widgetKey;
      setGeneratedKey(widgetKey);

      // Generate iframe embed code
      const iframeEmbed = `<iframe 
  src="https://app.pipsqueaknetscan.com/s2/public/widget/${widgetKey}" 
  style="${widgetFrameStyle(config.appearance)}"
  title="Security Scanner"
  data-pipsqueak-widget="${widgetKey}"
></iframe>

<script>
${widgetResizeScript(widgetKey, 'https://app.pipsqueaknetscan.com/s2/public/widget/' + widgetKey)}

// Programmatic control (optional)
window.PipsqueakWidget?.setTheme('${config.theme}');
${config.colors.primary ? `window.PipsqueakWidget?.setColors(${JSON.stringify(config.colors)});` : ''}
${config.heroImage ? `window.PipsqueakWidget?.setHeroImage('${config.heroImage}');` : ''}
</script>`;

      setEmbedCode(iframeEmbed);

      // Generate script tag for direct embedding (legacy support)
      const scriptEmbed = `<script>
(function() {
  const script = document.createElement('script');
  script.src = 'https://app.pipsqueaknetscan.com/s2/public/widget/${widgetKey}';
  script.async = true;
  script.onload = function() {
    if (window.PipsqueakWidget) {
      window.PipsqueakWidget.setTheme('${config.theme}');
      ${config.colors.primary ? `window.PipsqueakWidget.setColors(${JSON.stringify(config.colors)});` : ''}
      ${config.heroImage ? `window.PipsqueakWidget.setHeroImage('${config.heroImage}');` : ''}
    }
  };
  document.head.appendChild(script);
})();
</script>

<div id="widget-root"></div>`;

      setScriptTag(scriptEmbed);
      setCopied({ embed: false, script: false });

    } catch (err) {
      console.error('Failed to generate widget:', err);
      alert(`Failed to generate widget: ${err.message}`);
    }
  }, [config, errors, token]);

  const handleCopyToClipboard = useCallback(async (type) => {
    const textToCopy = type === 'embed' ? embedCode : scriptTag;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(prev => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [type]: false }));
      }, 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  }, [embedCode, scriptTag]);

  const handleAddFeature = useCallback(() => {
    const newFeature = prompt('Enter a new feature:');
    if (newFeature && newFeature.trim()) {
      updateConfig({
        features: [...config.features, newFeature.trim()]
      });
    }
  }, [config.features, updateConfig]);

  const handleRemoveFeature = useCallback((index) => {
    const newFeatures = [...config.features];
    newFeatures.splice(index, 1);
    updateConfig({ features: newFeatures });
  }, [config.features, updateConfig]);


  useEffect(() => {
    setEmbedCode('');
    setScriptTag('');
    setGeneratedKey('');
    setCopied({ embed: false, script: false });
  }, [config]);



  const tabs = [
    { id: 'basic', label: 'Basic', icon: <Settings size={18} /> },
    { id: 'layout', label: 'Layout', icon: <Layout size={18} /> },
    // { id: 'hero', label: 'Hero', icon: <Image size={18} /> },
    { id: 'form', label: 'Form', icon: <Type size={18} /> },
    { id: 'display', label: 'Display', icon: <Smartphone size={18} /> },
    { id: 'advanced', label: 'Advanced', icon: <Palette size={18} /> },
  ];

  return (
    <div className="widget-creator-container">
    
      {/* Main Content */}
      <main className={`creator-main ${themclass}`}>
        <header className="creator-header">
          <div className="header-content">
            <h2 className={themclass}>Create Your Attack Surface Lead Magnet</h2>
            <p className="header-subtitle">
              Configure every aspect of your widget with our intuitive controls
            </p>
          </div>
          
          <div className="header-actions">
            
            <div className="layout-theme-display">
              <div className="layout-display">
                <Layout size={16} /> 
                <span>{selectedLayout.name}</span>
              </div>
              <div className="theme-display">
                <div className="theme-dot" style={{ backgroundColor: selectedTheme.primary }} />
                <span>{selectedTheme.name}</span>
              </div>
            </div>
          </div>
        </header> 

        <div className="creator-content">
          {/* Tabs */}
          <div className="creator-tabs">
            {tabs.map(tab => ( 
              <button
                key={tab.id}
                className={`creator-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Basic Settings */}
            {activeTab === 'basic' && (
              <div className="config-group">
                <div className="config-header">
                  <h3 className={isDarkMode?"text-light":""}>
                    <Settings size={20} />
                    Basic Settings
                  </h3>
                  <p className="config-description">
                    Configure the fundamental properties of your widget
                  </p>
                </div>

                <div className="config-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <Type size={16} />
                      Widget Title
                    </label>
                    <input
                      type="text"
                      value={config.title}
                      onChange={(e) => updateConfig({ title: e.target.value })}
                      className={`form-input ${errors.title ? 'input-error' : ''}`}
                      placeholder="Security Scanner"
                    />
                    {errors.title && (
                      <div className="error-message">{errors.title}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <Type size={16} />
                      Subtitle (optional)
                    </label>
                    <input
                      type="text"
                      value={config.subtitle}
                      onChange={(e) => updateConfig({ subtitle: e.target.value })}
                      className="form-input"
                      placeholder="Scan your website for security vulnerabilities"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <Image size={16} />
                      Logo URL (optional)
                    </label>
                    <input
                      type="url"
                      value={config.logoUrl}
                      onChange={(e) => updateConfig({ logoUrl: e.target.value })}
                      className={`form-input ${errors.logoUrl ? 'input-error' : ''}`}
                      placeholder="https://example.com/logo.png"
                    />
                    {errors.logoUrl && (
                      <div className="error-message">{errors.logoUrl}</div>
                    )}
                    <div className="input-hint">
                      PNG or SVG recommended
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Layout Settings */}
            {activeTab === 'layout' && (
              <div className="config-group">
                <div className="config-header">
                  <h3 className={isDarkMode?"text-light":""}>
                    <Layout size={20} />
                    Layout & Theme
                  </h3>
                  <p className="config-description">
                    Choose how your widget will be structured and styled
                  </p>
                </div>

                <div className="config-section">
                  <h4>Layout Style</h4>
                  <div className="layout-grid">
                    {LAYOUTS.map(layout => (
                      <div 
                        key={layout.id}
                        className={`layout-card ${config.layout === layout.id ? 'selected' : ''}`}
                        onClick={() => updateConfig({ layout: layout.id })}
                      >
                        <div className="layout-card-preview" style={layout.style}>
                          {layout.id === 'split' ? (
                            <div className="layout-preview-split">
                              <div className="preview-hero"></div>
                              <div className="preview-form-area"></div>
                            </div>
                          ) : layout.id === 'centered' ? (
                            <div className="layout-preview-centered">
                              <div className="preview-header"></div>
                              <div className="preview-form-compact"></div>
                            </div>
                          ) : (
                            <div className="layout-preview-compact">
                              <div className="preview-form-mini"></div>
                            </div>
                          )}
                        </div>
                        <div className="layout-card-info">
                          <div className="layout-card-title">{layout.name}</div>
                          <div className="layout-card-desc">{layout.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="config-section">
                  <h4>Color Theme</h4>
                  <div className="theme-grid">
                    {THEMES.map(theme => (
                      <div 
                        key={theme.id}
                        className={`theme-card ${config.theme === theme.id ? 'selected' : ''}`}
                        onClick={() => updateConfig({ theme: theme.id })}
                        style={{ 
                          backgroundColor: theme.bg,
                          color: theme.id === 'dark' || theme.id === 'cyber' ? 'white' : '#1f2937'
                        }}
                      >
                        <div className="theme-card-header">
                          {theme.icon}
                          <span className="theme-card-name">{theme.name}</span>
                        </div>
                        <div className="theme-card-colors">
                          <div 
                            className="theme-color-dot"
                            style={{ backgroundColor: theme.primary }}
                          />
                          <div 
                            className="theme-color-dot"
                            style={{ backgroundColor: theme.bg }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Hero Settings */}
            {activeTab === 'hero' && config.layout === 'split' && (
              <div className="config-group">
                <div className="config-header">
                  <h3 className={isDarkMode?"text-light":""}>
                    <Image size={20} />
                    Hero Section
                  </h3>
                  <p className="config-description">
                    Customize the hero image and content for split layout
                  </p>
                </div>

                <div className="config-grid">
                  <div className="form-group full-width">
                    <label className="form-label">
                      <Image size={16} />
                      Hero Image URL
                    </label>
                    <input
                      type="url"
                      value={config.heroImage}
                      onChange={(e) => updateConfig({ heroImage: e.target.value })} 
                      className={`form-input ${errors.heroImage ? 'input-error' : ''}`}
                      placeholder="https://images.unsplash.com/photo-..."
                    />
                    {errors.heroImage && (
                      <div className="error-message">{errors.heroImage}</div>
                    )}
                    <div className="input-hint">
                      Use high-quality images from Unsplash or similar services
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <Type size={16} />
                      Hero Title
                    </label>
                    <input
                      type="text"
                      value={config.heroTitle}
                      onChange={(e) => updateConfig({ heroTitle: e.target.value })}
                      className="form-input"
                      placeholder="Secure Your Digital Presence"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <Type size={16} />
                      Hero Subtitle
                    </label>
                    <textarea
                      value={config.heroSubtitle}
                      onChange={(e) => updateConfig({ heroSubtitle: e.target.value })}
                      className="form-textarea"
                      placeholder="Get instant security insights with our advanced scanning technology"
                      rows="3"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">
                      <Check size={16} />
                      Features List
                    </label>
                    <div className="features-list">
                      {config.features.map((feature, index) => (
                        <div key={index} className="feature-tag">
                          <span>{feature}</span>
                          <button
                            type="button"
                            className="feature-remove"
                            onClick={() => handleRemoveFeature(index)}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="feature-add"
                        onClick={handleAddFeature}
                      >
                        <Plus size={16} />
                        Add Feature
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form Settings */}
            {activeTab === 'form' && (
              <div className="config-group">
                <div className="config-header">
                  <h3 className={isDarkMode?"text-light":""}>
                    <Type size={20} />
                    Form Settings
                  </h3>
                  <p className="config-description">
                    Configure the form fields and button appearance
                  </p>
                </div>
                
                <div className="config-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <Type size={16} />
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={config.buttonText}
                      onChange={(e) => updateConfig({ buttonText: e.target.value })}
                      className={`form-input ${errors.buttonText ? 'input-error' : ''}`}
                      placeholder="Start Security Scan"
                    />
                    {errors.buttonText && (
                      <div className="error-message">{errors.buttonText}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <Palette size={16} />
                      Button Color
                    </label>
                    <div className="color-picker-group">
                      <input
                        type="color"
                        value={config.buttonColor}
                        onChange={(e) => updateConfig({ buttonColor: e.target.value })}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={config.buttonColor}
                        onChange={(e) => updateConfig({ buttonColor: e.target.value })}
                        className="color-input"
                        placeholder="#8b5cf6"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={config.collectEmail}
                        onChange={(e) => updateConfig({ collectEmail: e.target.checked })}
                      />
                      Collect Email Address
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Display Settings */}
            {activeTab === 'display' && (
              <div className="config-group">
                <div className="config-header">
                  <h3 className={isDarkMode?"text-light":""}>
                    <Smartphone size={20} />
                    Display Settings
                  </h3>
                  <p className="config-description">
                    Control how and where your widget appears
                  </p>
                </div>
                
                <div className="config-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <Maximize2 size={16} />
                      Display Mode
                    </label>
                    <select
                      value={config.displayMode}
                      onChange={(e) => updateConfig({ displayMode: e.target.value })}
                      className="form-select"
                    >
                      {/* Inline results were retired: the widget opens the full report
                          for every scan, so New Tab is the only mode it honours. */}
                      <option value="new-tab">New Tab</option>
                    </select>
                  </div>

                  {config.displayMode === 'modal' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">
                          <Type size={16} />
                          Trigger Button Text
                        </label>
                        <input
                          type="text"
                          value={config.triggerText}
                          onChange={(e) => updateConfig({ triggerText: e.target.value })}
                          className={`form-input ${errors.triggerText ? 'input-error' : ''}`}
                          placeholder="Run Security Scan"
                        />
                        {errors.triggerText && (
                          <div className="error-message">{errors.triggerText}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <Globe size={16} />
                          Button Position
                        </label>
                        <select
                          value={config.triggerPosition}
                          onChange={(e) => updateConfig({ triggerPosition: e.target.value })}
                          className="form-select"
                        >
                          <option value="bottom-right">Bottom Right</option>
                          <option value="bottom-left">Bottom Left</option>
                          <option value="top-right">Top Right</option>
                          <option value="top-left">Top Left</option>
                          <option value="center">Center Bottom</option>
                        </select>
                      </div>

                      <div className="form-group full-width">
                        <label className="checkbox-group">
                          <input
                            type="checkbox"
                            checked={config.autoOpen}
                            onChange={(e) => updateConfig({ autoOpen: e.target.checked })}
                            className="checkbox-input"
                          />
                          <span className="checkbox-custom"></span>
                          <span className="checkbox-label">
                            Auto-open modal on page load
                          </span>
                        </label>
                      </div> 

                      {config.autoOpen && (
                        <div className="form-group">
                          <label className="form-label">
                            Open Delay (seconds)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={config.openDelay}
                            onChange={(e) => updateConfig({ openDelay: parseInt(e.target.value) || 0 })}
                            className={`form-input ${errors.openDelay ? 'input-error' : ''}`}
                            placeholder="0"
                          />
                          {errors.openDelay && (
                            <div className="error-message">{errors.openDelay}</div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div className="form-group full-width">
                    <div className="checkbox-row">
                      <label className="checkbox-group">
                        <input
                          type="checkbox"
                          checked={config.themeToggle}
                          onChange={(e) => updateConfig({ themeToggle: e.target.checked })}
                          className="checkbox-input"
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-label">
                          Include theme toggle button
                        </span>
                      </label>
                      
                      <label className="checkbox-group">
                        <input
                          type="checkbox"
                          checked={config.backgroundPattern}
                          onChange={(e) => updateConfig({ backgroundPattern: e.target.checked })}
                          className="checkbox-input"
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-label">
                          Add background pattern
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Settings */}
            {activeTab === 'advanced' && (
              <div className="config-group">
                <div className="config-header">
                  <h3 className={isDarkMode?"text-light":""}>
                    <Palette size={20} />
                    Advanced Customization
                  </h3>
                  <p className="config-description">
                    Fine-tune colors and advanced settings
                  </p>
                </div>

              

                {/* Legacy Webhook (collapsible) */}
                {/* <div className="config-section legacy-webhook-section">
                  <button
                    type="button"
                    className="legacy-webhook-toggle"
                    onClick={() => setShowLegacyWebhook(!showLegacyWebhook)}
                  >
                    <Lightning size={16} />
                    <span>Legacy Marketing Webhook</span>
                    {showLegacyWebhook ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  
                  {showLegacyWebhook && (
                    <div className="config-grid" style={{ marginTop: '12px' }}>
                      <div className="form-group full-width">
                        <label className="form-label">
                          <Lightning size={16} />
                          Marketing Webhook URL (optional)
                        </label>
                        <input
                          type="url"
                          value={config.webhookUrl}
                          onChange={(e) => updateConfig({ webhookUrl: e.target.value })}
                          className={`form-input ${errors.webhookUrl ? 'input-error' : ''}`}
                          placeholder="https://your-marketing-tool.com/webhook"
                        />
                        {errors.webhookUrl && (
                          <div className="error-message">{errors.webhookUrl}</div>
                        )}
                        <div className="input-hint">
                          We will send lead data to this URL whenever a report is unlocked
                        </div>
                      </div>
                    </div>
                  )}
                </div> */}

                <div className="config-section">
                  <h4>Custom Colors</h4>
                  <div className="color-customization-grid">
                    <div className="color-field">
                      <label className="color-label">
                        Primary Color
                      </label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          value={config.colors.primary || selectedTheme.primary}
                          onChange={(e) => updateConfig({
                            colors: { ...config.colors, primary: e.target.value }
                          })}
                          className="color-input"
                        />
                        <input
                          type="text"
                          value={config.colors.primary || ''}
                          onChange={(e) => updateConfig({
                            colors: { ...config.colors, primary: e.target.value }
                          })}
                          className="color-text-input"
                          placeholder={selectedTheme.primary}
                        />
                      </div>
                    </div>

                    <div className="color-field">
                      <label className="color-label">
                        Background Color
                      </label> 
                      <div className="color-input-group">
                        <input
                          type="color"
                          value={config.colors.background || selectedTheme.bg}
                          onChange={(e) => updateConfig({
                            colors: { ...config.colors, background: e.target.value }
                          })}
                          className="color-input"
                        />
                        <input
                          type="text"
                          value={config.colors.background || ''}
                          onChange={(e) => updateConfig({
                            colors: { ...config.colors, background: e.target.value }
                          })}
                          className="color-text-input"
                          placeholder={selectedTheme.bg}
                        />
                      </div>
                    </div>

                    <div className="color-field">
                      <label className="color-label">
                        Text Color
                      </label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          value={config.colors.text || '#334155'}
                          onChange={(e) => updateConfig({
                            colors: { ...config.colors, text: e.target.value }
                          })}
                          className="color-input"
                        />
                        <input
                          type="text"
                          value={config.colors.text || ''}
                          onChange={(e) => updateConfig({
                            colors: { ...config.colors, text: e.target.value }
                          })}
                          className="color-text-input"
                          placeholder="#334155"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="input-hint">
                    Custom colors will override theme defaults
                  </div>
                </div>
              </div>
            )}
            
            {/* Generate Button */}
            <div className="generate-button-container">
              <button 
                className="generate-widget-btn"
                onClick={handleGenerateWidget}
                disabled={Object.keys(errors).length > 0}
              >
                <Zap size={18} /> 
                Generate Widget
              </button>
            </div>

            {/* Output Section */}
            {(embedCode || scriptTag) && (
              <div className="output-section">
                <div className="output-header">
                  <h3>
                    <Code size={20} />
                    Embed Code
                  </h3>
                  <p className="output-description">
                    Copy and paste these codes into your website
                  </p>
                </div>

                <div className="output-content">
                  {embedCode && (
                    <div className="code-block">
                      <div className="code-header">
                        <span className="code-title">
                          <Globe size={16} />
                          Iframe Embed (Recommended)
                        </span>
                        <button 
                          className="copy-code-btn"
                          onClick={() => handleCopyToClipboard('embed')}
                        >
                          <Copy size={16} />
                          {copied.embed ? 'Copied!' : 'Copy'}
                        </button> 
                      </div>
                      <pre className="code-content">
                        <code>{embedCode}</code>
                      </pre>
                    </div>
                  )}

                  {scriptTag && (
                    <div className="code-block">
                      <div className="code-header">
                        <span className="code-title">
                          <Code size={16} />
                          Script Tag (Legacy)
                        </span>
                        <button 
                          className="copy-code-btn"
                          onClick={() => handleCopyToClipboard('script')}
                        >
                          <Copy size={16} />
                          {copied.script ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <pre className="code-content">
                        <code>{scriptTag}</code>
                      </pre>
                    </div>
                  )}
                  
                  {generatedKey && (
                    <div className="test-widget-section">
                      <h4>
                        <Download size={16} />
                        Test Your Widget
                      </h4>
                      <div className="test-url">
                        <code>https://app.pipsqueaknetscan.com/s2/public/widget/{generatedKey}</code>
                        <button
                          className="copy-url-btn"
                          onClick={() => navigator.clipboard.writeText(`https://app.pipsqueaknetscan.com/s2/public/widget/${generatedKey}`)}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                  {/* API Customization Section */}
 {/* API Customization Section */}
<div className="api-customization-section">
  <h4>
    <Cpu size={16} />
    API Customization
  </h4>
  <p className="api-description">
    For full control and custom integrations, use our API with your widget key
  </p>
  
  <div className="api-examples-grid">
    {/* Trigger Domain Scan */}
    <div className="api-example-card">
      <div className="api-example-header">
        <Zap size={16} />
        <h5>Trigger External Risk Scan</h5>
      </div>
      <pre className="api-code-example">
        <code>{`// JavaScript Example
fetch('https://app.pipsqueaknetscan.com/s2/public/domain-scan', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    widgetKey: '${generatedKey}',
    target: 'example.com',
    modules: [
      'dns',
      'ports',
      'ssl',
      'headers',
      'email',
      'typosquatting'
    ]
  })
})
.then(response => response.json())
.then(data => {
  console.log('Scan results:', data);
  console.log('Risk score:', data.riskScore);
});`}</code>
      </pre>
      <div className="api-params">
        <strong>Parameters:</strong>
        <ul>
          <li><code>widgetKey</code> (required): Your widget key</li>
          <li><code>target</code> (required): Domain to scan</li>
          <li><code>modules</code> (optional): Array of modules to run</li>
        </ul>
      </div>
    </div>

  
  </div>
  
  <div className="api-info-box">
    <div className="api-info-header">
      <Shield size={16} />
      <h5>Security Notes</h5>
    </div>
    <ul className="api-info-list">
      <li>Always use HTTPS for API requests</li>
      <li>Store your API tokens securely (environment variables)</li>
      <li>Validate all user input before passing to the API</li>
      <li>Implement rate limiting on your client</li>
      <li>Scan results are encrypted and stored in S3</li>
    </ul>
  </div>
  
</div>
                </div>
              </div>
            )}
          </div>
        </div>
      
      <LeadMagnets />
      </main>

    </div>
  );

};

export default WidgetCreator;
